// src/data/waterPanel.js (s101) — THE PASS A SCHEMA. Dependency-free on purpose:
// the page, the gate and the status generator all import this one file, so the
// definition of "done" cannot differ between what renders and what is counted.
//
// A city record carries:
//   waterPanel: {
//     source: "<operator document, edition, where read>",   // REQUIRED if any field is captured
//     year: 2023,              // edition year, when the document has one
//     accessed: "2026-09",     // OR the month it was read, for undated pages/FAQs (one of the two is required)
//     <field>: { s: 'captured', ... } | { s: 'not-published' }
//   }
// A field that is ABSENT has not been read yet. 'not-published' means the
// operator's own document was read and the value is verifiably not in it —
// that is a FINISHED answer, not an empty box.
//
// Item 1 (hardness) is NOT stored here. It is derived from hardnessSourcing,
// which already has its own single-source gate. One fact, one home.

export const STATUS = ['captured', 'not-published'];

// kind: 'num' → needs avg | value | range.  Bounds are plausibility limits for
// finished municipal water; outside them the gate fails and a human looks.
export const FIELDS = {
  ph:           { item: 2, label: 'pH',                    kind: 'num', unit: '',      min: 5,  max: 11 },
  alkalinity:   { item: 2, label: 'Alkalinity',            kind: 'num', unit: 'mg/L as CaCO\u2083', min: 1, max: 600 },
  index:        { item: 2, label: 'Corrosion index',       kind: 'index' },   // { kind:'Langelier'|'Aggressive'|'CCPP', value }
  iron:         { item: 3, label: 'Iron',                  kind: 'num', unit: 'mg/L',  min: 0,  max: 5 },
  manganese:    { item: 3, label: 'Manganese',             kind: 'num', unit: 'mg/L',  min: 0,  max: 2 },
  sodium:       { item: 4, label: 'Sodium',                kind: 'num', unit: 'mg/L',  min: 0,  max: 600 },
  chloride:     { item: 4, label: 'Chloride',              kind: 'num', unit: 'mg/L',  min: 0,  max: 800 },
  tds:          { item: 4, label: 'Total dissolved solids', kind: 'num', unit: 'mg/L', min: 5,  max: 3000 },
  disinfectant: { item: 5, label: 'Disinfectant',          kind: 'disinfectant' }, // { type }
  treatment:    { item: 6, label: 'Source and treatment',  kind: 'treatment' },    // { sources, train, softens }
  publishedBy:  { item: 7, label: 'How results are published', kind: 'publishedBy' }, // { unit }
};

export const DISINFECTANTS = ['free chlorine', 'chloramine', 'both (seasonal switch)', 'chlorine dioxide', 'ozone + chlorine', 'ozone + chloramine', 'uv + chlorine', 'uv + chloramine'];
export const PUBLISHED_BY = ['system', 'plant', 'zone', 'source'];
export const INDEX_KINDS = ['Langelier', 'Aggressive', 'CCPP', 'Ryznar'];

export const ITEMS = [
  { n: 1, short: 'Hardness',     label: 'Total hardness (flag if the label is CALCIUM hardness)' },
  { n: 2, short: 'pH/alk/index', label: 'pH / alkalinity / Langelier or Aggressive index' },
  { n: 3, short: 'Fe/Mn',        label: 'Iron and manganese' },
  { n: 4, short: 'Na/Cl/TDS',    label: 'Sodium, chloride, TDS' },
  { n: 5, short: 'Disinfectant', label: 'Disinfectant type (free chlorine vs chloramine)' },
  { n: 6, short: 'Treatment',    label: 'Source(s), treatment train, and whether the plant SOFTENS' },
  { n: 7, short: 'Published by', label: 'Publication shape (per plant / per zone / per source)' },
];

// Item 1: any hardnessSourcing shape except 'unlocated' is a finished answer.
export function hardnessDone(city) {
  const s = city?.hardnessSourcing?.shape;
  return !!s && s !== 'unlocated';
}

// → { 1:{done,missing:[]}, ... 7:{...} }.  An item is done when EVERY field in
// it is resolved (captured or not-published). Partial items list what is missing.
export function panelStatus(city) {
  const out = {};
  for (const it of ITEMS) out[it.n] = { done: false, missing: [] };
  out[1].done = hardnessDone(city);
  if (!out[1].done) out[1].missing.push('hardness');
  const p = city?.waterPanel || {};
  for (const [key, def] of Object.entries(FIELDS)) {
    const f = p[key];
    if (!f || !STATUS.includes(f.s)) out[def.item].missing.push(key);
  }
  for (const it of ITEMS.slice(1)) out[it.n].done = out[it.n].missing.length === 0;
  return out;
}

export function panelScore(city) {
  return Object.values(panelStatus(city)).filter((x) => x.done).length;
}

// ── display helpers ────────────────────────────────────────────────────────
export function fieldDisplay(key, f) {
  const def = FIELDS[key];
  if (!f || f.s !== 'captured') return null;
  if (def.kind === 'num') {
    const u = def.unit ? ` ${def.unit}` : '';
    const r = Array.isArray(f.range) ? `${f.range[0]}\u2013${f.range[1]}` : null;
    const centre = f.avg ?? f.value;
    if (f.nd) return 'not detected';
    if (centre != null && r) return `${centre}${u} (range ${r})`;
    if (centre != null) return `${f.lessThan ? '< ' : ''}${centre}${u}`;
    return `${r}${u}`;
  }
  if (def.kind === 'index') {
    const sign = (n) => `${n > 0 ? '+' : ''}${n}`;
    const r = Array.isArray(f.range) ? `${sign(f.range[0])} to ${sign(f.range[1])}` : null;
    const tail = (f.value ?? f.range?.[1]) < 0 && f.kind === 'Langelier' ? ' \u2014 undersaturated: this water dissolves scale rather than depositing it' : '';
    return `${f.kind} index ${f.value != null ? sign(f.value) : r}${f.value != null && r ? ` (range ${r})` : ''}${tail}`;
  }
  if (def.kind === 'disinfectant') return f.type;
  if (def.kind === 'publishedBy') return `per ${f.unit}`;
  if (def.kind === 'treatment') {
    const soft = f.softens === true ? 'The plant softens.' : f.softens === false ? 'No softening step.' : '';
    return [f.sources, f.train, soft].filter(Boolean).join(' \u00b7 ');
  }
  return null;
}

// s111 Phase 3A — what each captured number means at the glass, in ONE table.
// The city page prints a short flag that links to /methodology#<anchor>; the
// methodology page prints the full `about` and every rule from these same
// entries, so the two cannot drift. (Before s111 the full sentences printed on
// every city page: the chloramine line alone repeated on 99 pages.)
// Thresholds are this site's working rules for glass, not health limits; where
// one matches an EPA secondary (cosmetic) standard, `about` says so.
const valueOf = (f) => f.avg ?? f.value ?? (Array.isArray(f.range) ? f.range[1] : null);
export const FIELD_GUIDE = {
  hardness: { anchor: 'hardness', title: 'Hardness',
    about: 'Calcium and magnesium, reported as milligrams per litre of calcium carbonate (mg/L as CaCO\u2083). One grain per gallon is 17.12 mg/L. Hardness is what a drying drop leaves as a white ring; the bands are in the table above.',
    rules: [] },
  ph: { anchor: 'ph', title: 'pH',
    about: "pH 7 is neutral. EPA's secondary (cosmetic) guideline for drinking water is 6.5 to 8.5, and utilities often run toward the top of that range, or above it, to protect pipes.",
    rules: [{ label: 'pH 9 or above', when: (v) => v >= 9, flag: 'Strongly alkaline', text: 'Strongly alkaline finished water: do not let it dwell on anodised aluminium.' }] },
  alkalinity: { anchor: 'alkalinity', title: 'Alkalinity',
    about: "The water's buffering capacity, how strongly it resists a change in pH, reported as mg/L of calcium carbonate. With pH and hardness it decides whether water tends to leave scale or to dissolve it.",
    rules: [] },
  index: { anchor: 'corrosion-index', title: 'Corrosion index',
    about: 'Utilities report the Langelier Saturation Index or a similar measure (the Aggressive Index, calcium carbonate precipitation potential, or the Ryznar index); the table names which. On the Langelier scale a positive value means the water tends to deposit calcium carbonate scale and a negative value means it tends to dissolve it; the other indexes use their own scales.',
    rules: [] },
  iron: { anchor: 'iron', title: 'Iron',
    about: "EPA's secondary (cosmetic) standard for iron is 0.3 mg/L, the level at which iron begins to stain.",
    rules: [
      { label: '0.3 mg/L or more', when: (v) => v >= 0.3, flag: 'At the staining level', text: 'At or above the 0.3 mg/L staining threshold: rust-coloured film that acid cleaners do not shift.' },
      { label: 'below 0.3 mg/L', when: (v) => v < 0.3, flag: 'Below the staining level', text: 'Below the 0.3 mg/L staining threshold.' },
    ] },
  manganese: { anchor: 'manganese', title: 'Manganese',
    about: "EPA's secondary (cosmetic) standard for manganese is 0.05 mg/L.",
    rules: [
      { label: '0.05 mg/L or more', when: (v) => v >= 0.05, flag: 'At the staining level', text: 'At or above 0.05 mg/L: brown-black oxidative staining is possible on sills and frames.' },
      { label: 'below 0.05 mg/L', when: (v) => v < 0.05, flag: 'Below the staining level', text: 'Below the 0.05 mg/L staining threshold.' },
    ] },
  sodium: { anchor: 'sodium', title: 'Sodium',
    about: 'Sodium stays behind when water dries, as part of the film on the glass.',
    rules: [{ label: '60 mg/L or more', when: (v) => v >= 60, flag: 'Salt-film level', text: 'Enough to leave a salt film that pulls moisture back from the air.' }] },
  chloride: { anchor: 'chloride', title: 'Chloride',
    about: "Chloride residue is hygroscopic: it holds moisture on a surface that looks dry. EPA's secondary standard, 250 mg/L, is set for taste; this site flags a lower level because of what it does to frames.",
    rules: [{ label: '100 mg/L or more', when: (v) => v >= 100, flag: 'Elevated for frames', text: 'Elevated: hygroscopic residue and faster corrosion on aluminium frames and screens.' }] },
  tds: { anchor: 'tds', title: 'Total dissolved solids',
    about: "Everything that stays behind when the water evaporates, so it predicts spotting better than hardness alone. EPA's secondary standard is 500 mg/L.",
    rules: [
      { label: '300 mg/L or more', when: (v) => v >= 300, flag: 'Spots on dry-down', text: 'Spots on dry-down; a pure-water final rinse earns its keep.' },
      { label: '100 mg/L or less', when: (v) => v <= 100, flag: 'Often dries clean', text: 'Low enough that tap-rinsed glass often dries clean.' },
    ] },
  disinfectant: { anchor: 'disinfectant', title: 'Disinfectant',
    about: 'Utilities disinfect with free chlorine or with chloramine, chlorine combined with ammonia. Free chlorine fades from water left standing; chloramine lasts.',
    rules: [{ label: 'chloramine', when: (v, f) => /chloramine/.test(f.type || ''), flag: 'Lasts in the bucket', text: 'Chloramine will not gas off in a bucket, and it wears pure-water resin faster.' }] },
  treatment: { anchor: 'treatment', title: 'Source and treatment',
    about: "From the utility's own report or, for the treatment steps, from EPA's federal record for that water system.",
    rules: [{ label: 'softened at the plant', when: (v, f) => f.softens === true, flag: 'Softened at the plant', text: 'Softened at the plant: treatment, not geology, sets this hardness.' }] },
};

function guideRule(key, f) {
  if (!f || f.s !== 'captured') return null;
  const g = FIELD_GUIDE[key];
  if (!g) return null;
  const v = valueOf(f);
  return g.rules.find((r) => r.when(v, f)) || null;
}

// The short flag and its anchor, for the city page's table.
export function fieldFlag(key, f) {
  const r = guideRule(key, f);
  return r ? { flag: r.flag, anchor: FIELD_GUIDE[key].anchor } : null;
}

// The full sentence (kept for callers that want the long form).
export function fieldMeaning(key, f) {
  const r = guideRule(key, f);
  return r ? r.text : null;
}
